import { useState, useEffect, useCallback } from "react";
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
    Box,
    Container,
    Typography,
    Button,
    Stack,
    Paper,
    Alert,
    CircularProgress,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Chip,
} from "@mui/material";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import { ValueChainNode } from "./ValueChainNode";
import {
    buildValueChainFromData,
    calculateValueChainLayout,
    toggleNodeCollapse,
    collapseAllNodes,
    expandAllNodes,
} from "src/utils/valueChainUtils";

const nodeTypes = {
    valueChainNode: ValueChainNode,
};

interface ValueChainData {
    upstream: any[];
    downstream: any[];
    metadata?: {
        maxUpstreamDepth?: number;
        maxDownstreamDepth?: number;
        totalEntities?: number;
        upstreamCount?: number;
        downstreamCount?: number;
    };
}

export interface ValueChainGraphProps {
    data?: ValueChainData;
    rootEntity: {
        id: number;
        title: string;
        type: string;
        isIntermediate?: boolean;
        ownedByName?: string | null;
    };
    onEntityClick?: (entity: { id: number; title: string; type: string }) => void;
    height?: number;
    showControls?: boolean;
}

export const ValueChainGraph = ({
    data,
    rootEntity,
    onEntityClick,
    height = 600,
    showControls = true,
}: ValueChainGraphProps) => {
    const { t } = useTranslation();
    const [nodes, setNodes, onNodesChange] = useNodesState<any>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<any>([]);
    const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());
    const [layoutDirection, setLayoutDirection] = useState<"TB" | "LR">("TB");
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [centerTrigger, setCenterTrigger] = useState(0);

    const updateLayout = useCallback(
        (upstream: any[], downstream: any[], collapsed: Set<string>) => {
            if (!upstream && !downstream) return;

            const treeState = buildValueChainFromData(upstream || [], downstream || [], rootEntity, collapsed);
            const layoutedTree = calculateValueChainLayout(treeState.nodes, treeState.edges, layoutDirection);

            setNodes(layoutedTree.nodes);
            setEdges(layoutedTree.edges);
        },
        [layoutDirection, rootEntity, setNodes, setEdges],
    );

    useEffect(() => {
        if (data) {
            updateLayout(data.upstream, data.downstream, collapsedNodes);
        }
    }, [data, collapsedNodes, updateLayout]);

    const handleToggleCollapse = useCallback(
        (nodeId: string) => {
            if (!data) return;
            const newCollapsed = toggleNodeCollapse(nodeId, collapsedNodes);
            setCollapsedNodes(newCollapsed);
        },
        [data, collapsedNodes],
    );

    const handleExpandAll = useCallback(() => {
        if (!data) return;
        const newCollapsed = expandAllNodes();
        setCollapsedNodes(newCollapsed);
    }, [data]);

    const handleCollapseAll = useCallback(() => {
        if (!data) return;
        const newCollapsed = collapseAllNodes(nodes);
        setCollapsedNodes(newCollapsed);
    }, [data, nodes]);

    const handleNodeClick = useCallback(
        (entity: any) => {
            const nodeId = `${entity.type}-${entity.id}`;
            setSelectedNodeId(nodeId);
            onEntityClick?.(entity);
        },
        [onEntityClick],
    );

    const centerView = () => {
        setCenterTrigger((prev) => prev + 1);
    };

    const stats = data?.metadata;

    if (data === undefined) {
        return (
            <Container maxWidth="lg">
                <Box display="flex" justifyContent="center" alignItems="center" height={height}>
                    <CircularProgress />
                    <Typography variant="h6" sx={{ ml: 2 }}>
                        {t("value_chain.loading", "Loading value chain...")}
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (data === null) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error">{t("value_chain.error", "Error loading value chain.")}</Alert>
            </Container>
        );
    }

    if (!data.upstream && !data.downstream) {
        return (
            <Container maxWidth="lg">
                <Alert severity="info">{t("value_chain.no_data", "No value chain available for this product.")}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Stack spacing={3}>
                {showControls && (
                    <Paper sx={{ p: 2 }}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={2}
                        >
                            {/* Statistics */}
                            <Stack direction="row" spacing={2}>
                                {stats && (
                                    <>
                                        <Chip
                                            label={`${stats.upstreamCount || 0} ${t("value_chain.upstream", "upstream")}`}
                                        />
                                        <Chip
                                            label={`${stats.downstreamCount || 0} ${t("value_chain.downstream", "downstream")}`}
                                        />
                                        <Chip
                                            label={`${stats.totalEntities || 0} ${t("value_chain.entities", "entities")}`}
                                        />
                                        <Chip
                                            label={`${Math.max(stats.maxUpstreamDepth || 0, stats.maxDownstreamDepth || 0)} ${t("value_chain.depth", "levels")}`}
                                        />
                                    </>
                                )}
                            </Stack>

                            {/* Layout Controls */}
                            <FormControl>
                                <FormLabel component="legend" sx={{ fontSize: "0.8rem" }}>
                                    {t("value_chain.direction", "Direction")}
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={layoutDirection}
                                    onChange={(e) => setLayoutDirection(e.target.value as "TB" | "LR")}
                                >
                                    <FormControlLabel
                                        value="TB"
                                        control={<Radio size="small" />}
                                        label={t("value_chain.vertical", "Vertical")}
                                        sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
                                    />
                                    <FormControlLabel
                                        value="LR"
                                        control={<Radio size="small" />}
                                        label={t("value_chain.horizontal", "Horizontal")}
                                        sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
                                    />
                                </RadioGroup>
                            </FormControl>

                            {/* Action Buttons */}
                            <Stack direction="row" spacing={1}>
                                <Button
                                    startIcon={<ExpandMoreIcon />}
                                    onClick={handleExpandAll}
                                    size="small"
                                    variant="outlined"
                                >
                                    {t("value_chain.expand_all", "Expand all")}
                                </Button>
                                <Button
                                    startIcon={<ChevronRightIcon />}
                                    onClick={handleCollapseAll}
                                    size="small"
                                    variant="outlined"
                                >
                                    {t("value_chain.collapse_all", "Collapse all")}
                                </Button>
                                <Button
                                    startIcon={<RefreshIcon />}
                                    onClick={() => updateLayout(data.upstream, data.downstream, new Set())}
                                    size="small"
                                >
                                    {t("value_chain.reset", "Reset")}
                                </Button>
                                <Button startIcon={<CenterFocusStrongIcon />} onClick={centerView} size="small">
                                    {t("value_chain.center", "Center")}
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                )}

                {/* Value Chain Visualization */}
                <Box sx={{ height: height, border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
                    <ReactFlowProvider>
                        <ReactFlow
                            key={`reactflow-${centerTrigger}`}
                            nodes={nodes.map((node) => ({
                                ...node,
                                selected: node.id === selectedNodeId,
                                data: {
                                    ...node.data,
                                    onToggleCollapse: handleToggleCollapse,
                                    onNodeClick: handleNodeClick,
                                },
                            }))}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            nodeTypes={nodeTypes}
                            fitView
                            fitViewOptions={{
                                padding: 0.2,
                                includeHiddenNodes: false,
                            }}
                            minZoom={0.1}
                            maxZoom={2}
                            defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
                        >
                            <Background />
                            <Controls />
                            <MiniMap nodeColor={() => "#fff"} nodeBorderRadius={2} />
                        </ReactFlow>
                    </ReactFlowProvider>
                </Box>
            </Stack>
        </Container>
    );
};
